import {
  Avatar,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  Spacer,
  usePersistMuiTableState,
  dateFormatterDayMonthLongYearAndHourMinute2Digits,
} from "@qivia/ui";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useCallback, useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import "../../../../i18n/i18n";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import { documentDownloadXlsxAsync } from "../../../../libSlice/downloadFileXslx";
import {
  selectVehicleSessionsList,
  selectVehicleSessionsListStatus,
} from "../fleetSlice";
import {
  formatDateTime,
  formatMileage,
  getDateTime,
} from "../../../../libSlice/materialUiFormats";
import type { VehicleSession, VehicleSessionDisplayed } from "../fleetApi";

const pageName = "fleet.vehicleSessions";
const gridStorageKey = "vehicle-sessions-grid";

export const SharedInterfaceVehicleSessions = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const vehicleSessionsList = useAppSelector(selectVehicleSessionsList);
  const vehicleSessionsListStatus = useAppSelector(
    selectVehicleSessionsListStatus,
  );
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    gridStorageKey,
    pageName,
  );

  const [listDisplayed, setListDisplayed] = useState<
    VehicleSessionDisplayed[] | null
  >(null);

  const findKeysToTranslate = useCallback(
    (key: keyof VehicleSessionDisplayed, value: string) => {
      switch (key) {
        case "startOfUsage":
        case "endOfUsage":
          return dateFormatterDayMonthLongYearAndHourMinute2Digits(
            new Date(value),
          );
        case "isCabinCleaned":
        case "isTrunkCleaned":
          return t(`fleet.vehicleSessions.value.${value}`);
        default:
          return value;
      }
    },
    [t],
  );

  const keysToTranslate = useMemo(
    () =>
      [
        "startOfUsage",
        "endOfUsage",
        "isCabinCleaned",
        "isTrunkCleaned",
      ] as (keyof VehicleSessionDisplayed)[],
    [],
  );

  const vehicleSessionsDisplayed: (VehicleSession & {
    id: string;
  })[] = useMemo(() => {
    return vehicleSessionsList.map((session) => {
      let newDict: VehicleSession & { id: string } = {
        ...session,
        id: session.uuid,
      };
      Object.entries(session).forEach(([key, value]) => {
        if (keysToTranslate.includes(key as keyof VehicleSessionDisplayed)) {
          newDict = {
            ...newDict,
            [key]:
              value !== null
                ? findKeysToTranslate(
                    key as keyof VehicleSessionDisplayed,
                    value as string,
                  )
                : "",
          };
        }
      });
      return newDict;
    });
  }, [vehicleSessionsList, findKeysToTranslate, keysToTranslate]);

  useEffect(() => {
    if (vehicleSessionsListStatus === "success") {
      setListDisplayed(vehicleSessionsDisplayed);
    }
  }, [vehicleSessionsDisplayed, vehicleSessionsListStatus]);

  const searchBarProps = {
    values: vehicleSessionsDisplayed,
    setFilterValues: setListDisplayed,
    keysToIgnore: [
      "uuid" as const,
      "driverUuid" as const,
      "vehicleUuid" as const,
      "restitutions" as const,
    ],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("fleet.vehicleSessions.view.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof VehicleSessionDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      startOfUsage: {
        type: "date",
        width: 210,
        valueGetter: getDateTime,
        valueFormatter: formatDateTime,
      },
      endOfUsage: {
        type: "date",
        width: 210,
        valueGetter: getDateTime,
        valueFormatter: (value: string | Date) =>
          formatDateTime(value) || "-",
      },
      registrationNumber: {
        type: "string",
        valueFormatter: (value: string | null) => value ?? "-",
      },
      driverName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string) => {
          const initials = value?.split(" ").map((word) => word.charAt(0));
          return (
            value && (
              <StyledRow>
                <Avatar
                  color={"mint"}
                  size={"S"}
                  firstName={initials[0]}
                  lastName={initials[initials.length - 1]}
                />
                <Spacer x={0.75} />
                {value}
              </StyledRow>
            )
          );
        },
      },
      departureLocation: {
        type: "string",
        width: 320,
        valueFormatter: (value: string | null) => value ?? "-",
      },
      furthestDestination: {
        type: "string",
        width: 320,
        valueFormatter: (value: string | null) => value ?? "-",
      },
      mileage: {
        type: "number",
        valueGetter: (value: number | null) => value,
        valueFormatter: (value: number | null) =>
          value !== null ? formatMileage(value) : "-",
      },
      isCabinCleaned: {
        type: "string",
        valueFormatter: (value: string) => value || "-",
      },
      isTrunkCleaned: {
        type: "string",
        valueFormatter: (value: string) => value || "-",
      },
      observations: {
        type: "string",
        width: 380,
        valueFormatter: (value: string | null) => value ?? "-",
      },
    };
  }, []);

  if (!listDisplayed) {
    return <LaunchPage hasBorderRadius={true} />;
  }

  return (
    <StyledContainer>
      <MaterialUiTable<keyof VehicleSessionDisplayed, VehicleSessionDisplayed>
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
        downloadViewAction={downloadViewAction}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  height: 100%;
  flex-direction: column;
  width: 100%;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;
